import { useMemo } from 'react'
import { ComponentPropsType } from 'sitemodel/shared/types/sitemodel.type'
import { fetchYesObjectsWithFilteraction as defaultFetchYesObjectsWithFilteraction } from 'yesObject/shared/api/fetchYesObjectsWithFilteraction'
import useSitemodelState from '../useSitemodelState/useSitemodelState'
import { FilteractionObject } from '@yes.technology/react-toolkit'
import { FilteractionPayload } from './useSitemodelFilteractionParams'

const TOKEN_BETWEEN_CURLY_BRACES_REGEX = /\{\{([^}]+)\}\}/g

export const parseQuery = (
  query: string,
  state: ComponentPropsType = {}
): {
  interpolatedQuery: string
  dependencies: (string | undefined)[]
} => {
  let interpolatedQuery = query
  const dependencies: (string | undefined)[] = []

  const tokens = query.match(TOKEN_BETWEEN_CURLY_BRACES_REGEX)

  if (!tokens) {
    return { interpolatedQuery, dependencies }
  }

  interpolatedQuery = query.replaceAll(
    TOKEN_BETWEEN_CURLY_BRACES_REGEX,
    (_tokenWithCurlyBraces, token) => {
      const value = state[token]

      const isArrayStrings =
        Array.isArray(value) &&
        (value as unknown[]).every((val) => typeof val === 'string')

      if (isArrayStrings) {
        dependencies.push(value.toString())
        return value.join('", "')
      }

      const param = state[token]?.toString() ?? ''
      dependencies.push(state[token] === undefined ? undefined : param)
      return param
    }
  )

  return {
    interpolatedQuery,
    dependencies
  }
}

type UsePrepareFilteractionQueriesProps = {
  filteractionDescriptors: {
    [filteractionId: string]: {
      data?: FilteractionObject
      payload?: FilteractionPayload
    }
  }
  fetchYesObjectsWithFilteraction: typeof defaultFetchYesObjectsWithFilteraction
}

export default function usePrepareFilteractionQueries({
  fetchYesObjectsWithFilteraction,
  filteractionDescriptors
}: UsePrepareFilteractionQueriesProps) {
  const { sitemodelState } = useSitemodelState()

  const queries = useMemo(
    () =>
      Object.keys(filteractionDescriptors).flatMap((filteractionId) => {
        const descriptor = filteractionDescriptors[filteractionId].data
        const payload = filteractionDescriptors[filteractionId]?.payload

        if (!descriptor) {
          return []
        }

        const { interpolatedQuery, dependencies } = parseQuery(
          descriptor.query,
          sitemodelState
        )

        if (dependencies.some((dependency) => dependency === undefined)) {
          return []
        }

        const parsedFilteraction = {
          ...descriptor,
          query: interpolatedQuery
        }

        const queryFn = async () => {
          const { objects } = await fetchYesObjectsWithFilteraction({
            filteraction: parsedFilteraction,
            showLoading: false,
            projectFields: payload?.project_fields,
            pagination: payload?.pagination,
            orderby: payload?.orderby,
            skipRelations: payload?.skip_relations,
            skipMetadata: payload?.skip_metadata
          })

          return objects
        }

        const queryKey = [
          'filteraction-with-params',
          filteractionId,
          ...dependencies
        ]

        return {
          filteractionId,
          queryFn,
          queryKey
        }
      }),
    [filteractionDescriptors, sitemodelState, fetchYesObjectsWithFilteraction]
  )

  return queries
}
